import React, { createContext, useState, useContext, useEffect } from 'react';
import supabase from '../supabase/supabase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initial Session Check
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await fetchProfile(session.user);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error("Auth init error:", err);
        setLoading(false);
      }
    };

    initAuth();

    // Listen for Auth Changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session) {
        await fetchProfile(session.user);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (authUser) => {
    try {
      const { data, error } = await supabase
        .from('portal_users_1741860000000')
        .select('*, organisations_1741860000000(name)')
        .eq('id', authUser.id)
        .single();

      if (data) {
        setUser({
          ...authUser,
          profile: data,
          role: data.role,
          organisation_id: data.organisation_id,
          org: data.organisations_1741860000000?.name || 'Independent'
        });
      }
    } catch (err) {
      console.error("Profile fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const register = async (email, password, profileData, orgName) => {
    const { data, error: authError } = await supabase.auth.signUp({ email, password });
    if (authError) throw authError;
    if (!data.user) throw new Error("Authentication failed.");

    // Atomic Registration Call
    const { data: rpcData, error: rpcError } = await supabase.rpc('handle_registration_v4', {
      p_user_id: data.user.id,
      p_email: email,
      p_first_name: profileData.first_name,
      p_last_name: profileData.last_name,
      p_organisation_name: orgName,
      p_role: profileData.role
    });

    if (rpcError) throw rpcError;
    return data;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);